import { useCallback, useMemo, useState, type ReactNode } from "react"
import { env } from "@/config/env"
import type { UserSubscription } from "@/features/subscriptions"
import {
  ASTRO_MAP_REPORT_PLAN_CODES,
  AURA_SCAN_REPORT_PLAN_CODES,
  SOULMATE_REPORT_PLAN_CODES,
  STAR_REPORT_PLAN_CODES,
  TRUTHS_REPORT_PLAN_CODES,
} from "@/features/subscriptions/report-plan-codes"
import { useSubscriptionStore } from "@/stores/subscription-store"

import { WebAppContext, type OpenScreens, type WebAppContextValue } from "./webapp-context"

/**
 * Daily "what's new" markers shown as badges in the webapp nav / Today screen.
 * Ported from lpr-web's WebAppProvider. The astro-today fetch and report catalog
 * (Today-page concerns) are intentionally deferred until that page is built.
 */
const DEFAULT_MARKERS_STATE: Record<string, boolean> = {
  horoscope: true,
  tarot: true,
  runes: true,
  moon: true,
  numerology: true,
  biorhythm: true,
}

const DEFAULT_OPEN_SCREENS_STATE: OpenScreens = {
  tarot: false,
  runes: false,
  numerology: false,
}

const MARKERS_STORAGE_KEY = "todayMarkers"
const REPORTS_BUNDLE = env.VITE_SUBSCRIPTION_BUNDLE

function hasPurchasedReportByPlan(
  subscriptions: UserSubscription[],
  reportPlanCodes: readonly string[],
): boolean {
  return subscriptions.some((subscription) => {
    const bundleMatches = REPORTS_BUNDLE ? subscription.bundle === REPORTS_BUNDLE : true
    return (
      bundleMatches &&
      reportPlanCodes.includes(subscription.planCode) &&
      subscription.status !== "expired"
    )
  })
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10)
}

function readMarkers(): { markers: Record<string, boolean>; screens: OpenScreens } {
  try {
    const raw = window.localStorage.getItem(MARKERS_STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed.date === todayKey()) {
        return { markers: parsed.markers, screens: parsed.screens }
      }
    }
  } catch {
    /* fall through to defaults */
  }
  return { markers: DEFAULT_MARKERS_STATE, screens: DEFAULT_OPEN_SCREENS_STATE }
}

export function WebAppProvider({ children }: { children: ReactNode }) {
  const subscription = useSubscriptionStore((s) => s.subscription)
  const subscriptions = useSubscriptionStore((s) => s.subscriptions)
  const isCanceled = useSubscriptionStore((s) => s.isCanceled)
  const verify = useSubscriptionStore((s) => s.verify)

  const initial = useMemo(() => readMarkers(), [])
  const [hasNewMarkers, setHasNewMarkers] = useState<Record<string, boolean>>(initial.markers)
  const [openedDailyScreens, setOpenedDailyScreens] = useState<OpenScreens>(initial.screens)
  const [selectedSign, setSelectedSign] = useState("")
  const [selectedTimeRange, setSelectedTimeRange] = useState("daily")
  const [isHoroscopeLoading, setIsHoroscopeLoading] = useState(false)
  const [headerHidden, setHeaderHidden] = useState(false)
  const [bottomInset, setBottomInset] = useState(0)

  const removeMarker = useCallback(
    (marker: string) => {
      setHasNewMarkers((prev) => {
        const newMarkers = { ...prev, [marker]: false }
        try {
          window.localStorage.setItem(
            MARKERS_STORAGE_KEY,
            JSON.stringify({
              date: todayKey(),
              markers: newMarkers,
              screens: { ...openedDailyScreens, [marker]: true },
            }),
          )
        } catch {
          /* ignore */
        }
        return newMarkers
      })
    },
    [openedDailyScreens],
  )

  const isStarReportPurchased = useMemo(
    () => hasPurchasedReportByPlan(subscriptions, STAR_REPORT_PLAN_CODES),
    [subscriptions],
  )
  const isTruthsReportPurchased = useMemo(
    () => hasPurchasedReportByPlan(subscriptions, TRUTHS_REPORT_PLAN_CODES),
    [subscriptions],
  )
  const isSoulmateReportPurchased = useMemo(
    () => hasPurchasedReportByPlan(subscriptions, SOULMATE_REPORT_PLAN_CODES),
    [subscriptions],
  )
  const isAstroMapReportPurchased = useMemo(
    () => hasPurchasedReportByPlan(subscriptions, ASTRO_MAP_REPORT_PLAN_CODES),
    [subscriptions],
  )
  const isAuraScanReportPurchased = useMemo(
    () => hasPurchasedReportByPlan(subscriptions, AURA_SCAN_REPORT_PLAN_CODES),
    [subscriptions],
  )

  const value: WebAppContextValue = {
    hasNewMarkers,
    removeMarker,
    openedDailyScreens,
    setOpenedDailyScreens,
    userSubscription: subscription,
    isSubscriptionCanceled: isCanceled,
    activeSubscriptionId: subscription?.id ?? null,
    checkUserSubscriptions: verify,
    isStarReportPurchased,
    isTruthsReportPurchased,
    isSoulmateReportPurchased,
    isAstroMapReportPurchased,
    isAuraScanReportPurchased,
    selectedSign,
    setSelectedSign,
    selectedTimeRange,
    setSelectedTimeRange,
    isHoroscopeLoading,
    setIsHoroscopeLoading,
    headerHidden,
    setHeaderHidden,
    bottomInset,
    setBottomInset,
  }

  return <WebAppContext.Provider value={value}>{children}</WebAppContext.Provider>
}
